import { supabase } from "@/integrations/supabase/client";

interface LogAdminActionParams {
  action_type: string;
  resource_type: string;
  resource_id?: string;
  action_details?: Record<string, any>;
}

export async function logAdminAction({
  action_type,
  resource_type,
  resource_id,
  action_details = {},
}: LogAdminActionParams): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get admin user record
    const { data: adminUser } = await supabase
      .from('admin_users')
      .select('id')
      .eq('user_id', user.id)
      .single();

    if (!adminUser) return;

    // Get client info
    const userAgent = navigator.userAgent;
    
    // Log the action
    await supabase.from('admin_audit_log').insert({
      admin_user_id: adminUser.id,
      action_type,
      resource_type,
      resource_id,
      action_details,
      user_agent: userAgent,
    });
  } catch (error) {
    console.error('Error logging admin action:', error);
  }
}

// Convenience functions for common actions
export const auditLog = {
  templateCreated: (templateId: string, details?: Record<string, any>) =>
    logAdminAction({
      action_type: 'create_template',
      resource_type: 'document_template',
      resource_id: templateId,
      action_details: details,
    }),

  templateUpdated: (templateId: string, details?: Record<string, any>) =>
    logAdminAction({
      action_type: 'update_template',
      resource_type: 'document_template',
      resource_id: templateId,
      action_details: details,
    }),

  templateDeleted: (templateId: string, details?: Record<string, any>) =>
    logAdminAction({
      action_type: 'delete_template',
      resource_type: 'document_template',
      resource_id: templateId,
      action_details: details,
    }),

  workflowCreated: (workflowId: string, details?: Record<string, any>) =>
    logAdminAction({
      action_type: 'create_workflow',
      resource_type: 'workflow',
      resource_id: workflowId,
      action_details: details,
    }),

  workflowUpdated: (workflowId: string, details?: Record<string, any>) =>
    logAdminAction({
      action_type: 'update_workflow',
      resource_type: 'workflow',
      resource_id: workflowId,
      action_details: details,
    }),

  aiKnowledgeCreated: (entryId: string, details?: Record<string, any>) =>
    logAdminAction({
      action_type: 'create_ai_knowledge',
      resource_type: 'ai_knowledge_entry',
      resource_id: entryId,
      action_details: details,
    }),

  aiKnowledgeUpdated: (entryId: string, details?: Record<string, any>) =>
    logAdminAction({
      action_type: 'update_ai_knowledge',
      resource_type: 'ai_knowledge_entry',
      resource_id: entryId,
      action_details: details,
    }),

  userManaged: (userId: string, action: string, details?: Record<string, any>) =>
    logAdminAction({
      action_type: `manage_user_${action}`,
      resource_type: 'user',
      resource_id: userId,
      action_details: details,
    }),

  systemSettingUpdated: (settingKey: string, details?: Record<string, any>) =>
    logAdminAction({
      action_type: 'update_system_setting',
      resource_type: 'system_setting',
      resource_id: settingKey,
      action_details: details,
    }),

  featureFlagToggled: (flagId: string, enabled: boolean, details?: Record<string, any>) =>
    logAdminAction({
      action_type: enabled ? 'enable_feature_flag' : 'disable_feature_flag',
      resource_type: 'feature_flag',
      resource_id: flagId,
      action_details: details,
    }),
};
